import { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { useApi, discoveryApiRef, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface HumanitecProject {
  id: string;
  display_name?: string;
}

/**
 * HumanitecProjectPickerComponent
 *
 * Fetches Humanitec v2 projects from the Backstage proxy
 * (/api/proxy/humanitec-v2/orgs/{orgId}/projects) and presents them as
 * a searchable select. The orgId is read from app-config `humanitecV2.orgId`.
 *
 * Falls back to a free-form text input if the proxy call fails.
 */
const HumanitecProjectPickerComponent = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [projects, setProjects] = useState<HumanitecProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const orgId =
        config.getOptionalString('app.humanitecOrgId') ?? 'skillful-wild-chicken-2617';
      try {
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const res = await fetchApi.fetch(
          `${proxyBase}/humanitec-v2/orgs/${orgId}/projects`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        // Humanitec v2 API wraps results: { items: [...] }
        const data: HumanitecProject[] = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
        if (!cancelled) setProjects(data);
      } catch (e: any) {
        if (!cancelled) setFetchError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <CircularProgress size={24} />;
  }

  // Graceful fallback: free-form text input when projects can't be fetched
  if (fetchError || projects.length === 0) {
    return (
      <TextField
        label="Humanitec Project"
        required={required}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value)}
        error={(rawErrors?.length ?? 0) > 0}
        helperText={
          fetchError
            ? `Could not load projects (${fetchError}) — enter project ID manually`
            : 'Enter the Humanitec project ID'
        }
        fullWidth
      />
    );
  }

  return (
    <FormControl fullWidth required={required} error={(rawErrors?.length ?? 0) > 0}>
      <InputLabel>Humanitec Project</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        {projects.map(p => (
          <MenuItem key={p.id} value={p.id}>
            {p.display_name ? `${p.display_name} (${p.id})` : p.id}
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && (
        <FormHelperText>{rawErrors![0]}</FormHelperText>
      )}
    </FormControl>
  );
};

export const HumanitecProjectPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'HumanitecProjectPicker',
    component: HumanitecProjectPickerComponent,
  }),
);
